// src/components/ReportHeader/controls/AreaSelectorControl.js
import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import useAreas from 'hooks/clients/grm/useAreas';

/**
 * Area Selector Control with useAreas Hook Integration
 * Fixed to work with the actual data structure from useAreas
 */
function AreaSelectorControl({ 
  value = "Brand",
  onChange, 
  label = "Area",
  variant = 'yellow',
  disabled = false,
  sx = {},
  // New props for access control
  filteredAreas = null,
  hasFullAccess = true,
  ...props 
}) {
  // Use the areas hook only if filteredAreas not provided (for backward compatibility)
  const areasData = useAreas();
  console.log('areasData', areasData)
  
  // Extract the raw data array and filter for company areas
  const { data, status, error } = areasData || {};
  
  // Create company areas from the raw data or use filtered areas if provided
  const companyAreas = React.useMemo(() => {
    let areas = [];

    // If filteredAreas is provided (from access control), use those
    if (filteredAreas && Array.isArray(filteredAreas)) {
      areas = filteredAreas
        .filter(area => area.areaType === 'Company')
        .map(area => ({
          id: area.areaId,
          name: area.areaName,
          storeCount: area.stores ? area.stores.filter(store =>
            store.status !== 'Closed'
          ).length : 0,
          manager: area.areaManager
        }))
        // Filter out areas with no active stores
        .filter(area => area.storeCount > 0);
    } else if (data && Array.isArray(data)) {
      // Fallback to original logic
      areas = data
        .filter(area => area.areaType === 'Company') // Use areaType, not type
        .map(area => ({
          id: area.areaId,
          name: area.areaName, // Use areaName, not name
          storeCount: area.stores ? area.stores.filter(store =>
            store.status !== 'Closed'
          ).length : 0,
          manager: area.areaManager
        }))
        // Filter out areas with no active stores
        .filter(area => area.storeCount > 0);
    }

    // Sort areas alphabetically by name
    return areas.sort((a, b) => a.name.localeCompare(b.name));
  }, [data, filteredAreas]);
  
  // Debug logging
  console.log('AreaSelectorControl Debug:', {
    rawData: data,
    companyAreas,
    status,
    error,
    value,
    disabled
  });
  
  // Get control styles based on variant
  const getControlStyles = (variant) => {
    const styles = {
      light: {
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(255,255,255,0.1)',
          color: 'white',
          '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
          '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
        },
        '& .MuiInputBase-input': { color: 'white' },
        '& .MuiSvgIcon-root': { color: 'white' },
        '& .MuiInputLabel-root': { 
          color: 'rgba(255,255,255,0.7)',
          '&.Mui-focused': { color: 'rgba(255,255,255,0.9)' }
        }
      },
      yellow: {
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#29261f',
          color: '#cf9e0b',
          '& fieldset': { borderColor: '#cf9e0b' },
          '&:hover fieldset': { borderColor: '#cf9e0b' },
          '&.Mui-focused fieldset': { borderColor: '#cf9e0b' }
        },
        '& .MuiInputBase-input': { color: '#cf9e0b' },
        '& .MuiSvgIcon-root': { color: '#cf9e0b' },
        '& .MuiInputLabel-root': { 
          color: '#cf9e0b',
          opacity: 0.8,
          '&.Mui-focused': { color: '#cf9e0b', opacity: 1 }
        }
      }
    };
    
    return styles[variant] || styles.light;
  };

  // Debug: Check visibility conditions
  const isLoading = status === 'PENDING';
  const hasCompanyAreas = companyAreas && companyAreas.length > 0;
  const hasError = !!error;
  
  // Determine if control should be disabled
  const isControlDisabled = disabled || isLoading || hasError || !hasCompanyAreas;
  
  console.log('AreaSelectorControl Visibility Check:', {
    isLoading,
    hasCompanyAreas,
    hasError,
    isControlDisabled,
    alwaysVisible: true // Now always visible
  });

  // Always render the control, but disable when appropriate
  const handleChange = (event) => {
    const selectedValue = event.target.value;
    console.log('AreaSelectorControl: Area changed to:', selectedValue);
    onChange?.(selectedValue);
  };

  // Determine what areas to show
  let areasToShow = companyAreas;
  if (!hasCompanyAreas) {
    // Show placeholder when no data
    areasToShow = [];
  }

  console.log('AreaSelectorControl: Always rendering dropdown. Disabled:', isControlDisabled, 'Areas count:', areasToShow.length);

  return (
    <FormControl 
      size="small" 
      sx={{ 
        minWidth: 200,
        maxWidth: 250,
        ...getControlStyles(variant),
        ...sx
      }}
      disabled={isControlDisabled}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={handleChange}
        label={label}
        {...props}
      >
        {/* Top level option - Area Summary for full access, Area for restricted */}
        <MenuItem value={hasFullAccess ? "Area Summary" : "Area"}>
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {hasFullAccess ? "Area Summary" : "Area"}
            </Typography>
          </Box>
        </MenuItem>

        {/* All Stores option - shows all stores alphabetically */}
        <MenuItem value="All Stores">
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              All Stores
            </Typography>
          </Box>
        </MenuItem>

        {/* Show areas if available, otherwise show loading/error state */}
        {isLoading && (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={16} sx={{ color: '#cf9e0b', mr: 1 }} />
              <Typography variant="body2" sx={{ color: '#cf9e0b', opacity: 0.7 }}>
                Loading areas...
              </Typography>
            </Box>
          </MenuItem>
        )}

        {hasError && !isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" sx={{ color: '#e74c3c', opacity: 0.7 }}>
              Error loading areas
            </Typography>
          </MenuItem>
        )}

        {!isLoading && !hasError && !hasCompanyAreas && (
          <MenuItem disabled>
            <Typography variant="body2" sx={{ color: '#cf9e0b', opacity: 0.7 }}>
              No areas available
            </Typography>
          </MenuItem>
        )}

        {/* Company areas when available */}
        {areasToShow.map((area) => (
          <MenuItem key={area.id} value={area.name}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
              <Typography variant="body2">
                {area.name}
              </Typography>
              {area.storeCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    opacity: 0.7,
                    fontSize: '0.75rem'
                  }}
                >
                  ({area.storeCount} stores)
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// PropTypes for development validation
AreaSelectorControl.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['light', 'yellow']),
  disabled: PropTypes.bool,
  sx: PropTypes.object
};

export default AreaSelectorControl;